import React, { useState, useEffect, useRef } from 'react';
import { Table, Alert, Space, Typography, Tag, Collapse, Input, InputNumber } from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  WarningOutlined,
  EditOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// Editable cell component
/* eslint-disable react/prop-types */
const EditableCell = ({
  editing,
  dataIndex,
  inputType,
  record,
  children,
  onSave,
  ...restProps
}) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState(record?.[dataIndex]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    setValue(record?.[dataIndex]);
  }, [record, dataIndex]);

  const handleSave = () => {
    if (onSave) {
      onSave(record._rowKey, dataIndex, value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    }
    if (e.key === 'Escape') {
      setValue(record?.[dataIndex]);
    }
  };

  const inputNode = inputType === 'number' ? (
    <InputNumber
      ref={inputRef}
      value={value}
      onChange={(val) => setValue(val)}
      onBlur={handleSave}
      onKeyDown={handleKeyDown}
      style={{ width: '100%' }}
      size="small"
    />
  ) : (
    <Input
      ref={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleSave}
      onKeyDown={handleKeyDown}
      size="small"
    />
  );

  return (
    <td {...restProps}>
      {editing ? inputNode : children}
    </td>
  );
};

// Add stable identifiers to each invoice:
// - _originalIndex: 0-based array index for onInvoiceUpdate callback
// - _originalRow: CSV row number for validation error matching
const addStableIds = (invoiceList) =>
  invoiceList.map((invoice, idx) => ({
    ...invoice,
    _originalIndex: idx, // 0-based index for updating the correct invoice in parent state
    _originalRow: idx + 2, // +2 because CSV row 1 is header, data starts at row 2
  }));

/* eslint-disable react/prop-types */
const PreviewDataStep = ({ invoices, validationErrors, adjustedRows = new Set(), adjustedCells = new Set(), csvFileName, onInvoiceUpdate }) => {
  const [searchText, setSearchText] = useState('');
  // Initialize with stable IDs already applied so the first render has valid rowKey values
  const [filteredInvoices, setFilteredInvoices] = useState(() => addStableIds(invoices));
  const [editingKey, setEditingKey] = useState('');
  const [editingColumn, setEditingColumn] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Update filtered invoices when invoices or search text change
  useEffect(() => {
    const invoicesWithStableIds = addStableIds(invoices);

    if (!searchText) {
      setFilteredInvoices(invoicesWithStableIds);
    } else {
      const filtered = invoicesWithStableIds.filter(invoice =>
        Object.values(invoice).some(val =>
          String(val).toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setFilteredInvoices(filtered);
    }
  }, [invoices, searchText]);

  const isEditing = (record, dataIndex) => record._rowKey === editingKey && dataIndex === editingColumn;

  const handleCellClick = (record, dataIndex) => {
    if (onInvoiceUpdate) {
      setEditingKey(record._rowKey);
      setEditingColumn(dataIndex);
    }
  };

  const handleSave = (rowKey, dataIndex, value) => {
    setEditingKey('');
    setEditingColumn('');
    
    if (onInvoiceUpdate) {
      onInvoiceUpdate(rowKey, dataIndex, value);
    }
  };

  const isAdjusted = (record) => adjustedRows.has(record._originalRow);

  const blockingErrors = (validationErrors || []).filter(e => !adjustedRows.has(e.row));
  const hasErrors = blockingErrors.length > 0;

  const getHighlightedColumn = (errorMsg) => {
    if (!errorMsg) return null;
    if (errorMsg.includes('Expense code') || errorMsg.includes('Expense Code')) return 'ExpenseCode';
    if (errorMsg.includes('GLCompanyDepartmentKey')) return 'Department';
    if (errorMsg.includes('GLAccountKey')) return 'BankCode';
    if (errorMsg.includes('exists for vendor')) return 'VendorLocationKey';
    return null;
  };

  const getRecordHighlightedColumn = (record) => {
    if (isAdjusted(record)) return null;
    const error = validationErrors.find(e => e.row === record._originalRow);
    return error ? getHighlightedColumn(error.errors[0]) : null;
  };

  const getCellClassName = (record, dataIndex) => {
    if (adjustedCells.has(`${record._originalRow}|${dataIndex}`)) return 'adjusted-cell';
    if (isAdjusted(record)) return 'adjusted-cell-bg';
    return getRecordHighlightedColumn(record) === dataIndex ? 'error-cell' : undefined;
  };

  // Just update searchText - useEffect handles the actual filtering
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Helper to create editable column config
  const createEditableColumn = (title, dataIndex, width, inputType = 'text', customRender = null) => ({
    title: (
      <Space size={4}>
        {title}
        {onInvoiceUpdate && <EditOutlined style={{ fontSize: 10, color: '#1890ff' }} />}
      </Space>
    ),
    dataIndex,
    key: dataIndex,
    width,
    editable: true,
    inputType,
    onCell: (record) => ({
      record,
      dataIndex,
      title,
      inputType,
      editing: isEditing(record, dataIndex),
      onSave: handleSave,
      onClick: () => handleCellClick(record, dataIndex),
      className: getCellClassName(record, dataIndex),
      style: { cursor: onInvoiceUpdate ? 'pointer' : 'default' }
    }),
    render: customRender || ((text) => text)
  });

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'InvoiceNumber',
      key: 'InvoiceNumber',
      fixed: 'left',
      width: 150,
      editable: true,
      inputType: 'text',
      onCell: (record) => ({
        record,
        dataIndex: 'InvoiceNumber',
        title: 'Invoice #',
        inputType: 'text',
        editing: isEditing(record, 'InvoiceNumber'),
        onSave: handleSave,
        onClick: () => handleCellClick(record, 'InvoiceNumber'),
        className: getCellClassName(record, 'InvoiceNumber'),
        style: { cursor: onInvoiceUpdate ? 'pointer' : 'default' }
      }),
      render: (text, record) => {
        const error = validationErrors.find(e => e.row === record._originalRow);
        return (
          <Space>
            {isAdjusted(record) ? (
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            ) : error ? (
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            )}
            {text}
            {onInvoiceUpdate && <EditOutlined style={{ fontSize: 10, color: '#1890ff' }} />}
          </Space>
        );
      }
    },
    createEditableColumn('Company Key', 'CompanyKey', 120, 'number'),
    createEditableColumn('Vendor Location', 'VendorLocationKey', 150, 'number'),
    createEditableColumn('Amount', 'Amount', 120, 'number', (amount) => `$${parseFloat(amount || 0).toFixed(2)}`),
    createEditableColumn('Invoice Date', 'InvoiceDate', 130),
    createEditableColumn('Due Date', 'DueDate', 130),
    createEditableColumn('Department', 'Department', 150),
    createEditableColumn('Bank Code', 'BankCode', 120),
    createEditableColumn('Expense Code', 'ExpenseCode', 120),
    createEditableColumn('FileName', 'FileName', 250, 'text', (text) => {
      if (!text) return '-';
      // Show only file name, full path in tooltip
      const displayName = text.split(/[/\\]/).pop() || text;
      return <span title={text}>{displayName}</span>;
    }),
    createEditableColumn('Memo', 'Memo', 200),
    {
      title: 'Status',
      key: 'status',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        if (isAdjusted(record)) {
          return <Tag color="warning">Adjusted</Tag>;
        }
        const error = validationErrors.find(e => e.row === record._originalRow);
        return error ? (
          <Tag color="error">Invalid</Tag>
        ) : (
          <Tag color="success">Valid</Tag>
        );
      }
    }
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: col.onCell
    };
  });

  const totalAmount = invoices.reduce((sum, inv) => sum + parseFloat(inv.Amount || 0), 0);
  const totalInvoices = new Set(invoices.map(i => i.InvoiceNumber).filter(Boolean)).size;

  return (
    <div className="preview-data-step">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Preview Invoice Data</Title>
          <Text type="secondary">
            Review the parsed invoice data before uploading to VMS system
          </Text>
        </div>

        <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>File: {csvFileName}</Text>
            <Text strong>Total Invoices: {totalInvoices}</Text>
            <Text strong>Total Rows: {invoices.length}</Text>
            <Text strong>Total Amount: ${totalAmount.toFixed(2)}</Text>
          </Space>
        </div>

        {hasErrors && (
          <Alert
            message={<><WarningOutlined /> Validation Errors Found</>}
            description={
              <div>
                <Text>
                  {new Set(blockingErrors.map(e => e.errors.join('|'))).size} error(s) found.
                  {onInvoiceUpdate ? (
                    <strong> Click on any cell to edit directly in the table.</strong>
                  ) : (
                    ' Please fix these errors in your CSV file before proceeding.'
                  )}
                </Text>
                <Collapse
                  style={{ marginTop: 16 }}
                  items={blockingErrors
                    .filter((error, index, self) =>
                      index === self.findIndex(e => e.errors.join('|') === error.errors.join('|'))
                    )
                    .map((error, index) => ({
                      key: index,
                      label: error.errors[0],
                      children: (
                        <div style={{ color: '#ff4d4f' }}>
                          {error.errors.slice(1).map((err, i) => (
                            <div key={i}>{err}</div>
                          ))}
                        </div>
                      )
                    }))}
                />
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {!hasErrors && (
          <Alert
            message={<><CheckCircleOutlined /> {adjustedRows.size > 0 ? 'Ready to Upload' : 'All Invoices Valid'}</>}
            description={adjustedRows.size > 0
              ? "No invalid invoices remaining. Adjusted invoices will be revalidated on upload."
              : "All invoices have passed validation. Click 'Upload & Create Batch' to proceed."}
            type="success"
            showIcon
          />
        )}

        <Input
          id="vmsinvoice-preview-search-input"
          placeholder="Search invoices..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          size="large"
        />

        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          columns={mergedColumns}
          dataSource={filteredInvoices.map((invoice) => ({ ...invoice, _rowKey: invoice._originalIndex }))}
          rowKey="_rowKey"
          scroll={{ x: 1500, y: 400 }}
          pagination={{
            current: Math.min(
              currentPage,
              Math.max(1, Math.ceil(filteredInvoices.length / pageSize) || 1),
            ),
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} invoices`,
            onChange: (page, size) => {
              if (size !== pageSize) {
                // Page size changed: reset to first page and update size
                setCurrentPage(1);
                setPageSize(size);
              } else {
                // Only page changed
                setCurrentPage(page);
              }
            }
          }}
          size="small"
          rowClassName={(record) => {
            if (isAdjusted(record)) return 'editable-row';
            const error = validationErrors.find(e => e.row === record._originalRow);
            return error ? 'editable-row error-row' : 'editable-row';
          }}
        />
      </Space>
    </div>
  );
};

export default PreviewDataStep;
